<template>
  <div class="survey-editor-container">
    <el-form
      ref="surveyForm"
      :model="form"
      :rules="validationRules"
      label-width="120px"
      class="survey-form"
      size="medium"
    >
      <!-- 1. 基本信息区域 -->
      <el-card shadow="hover" class="form-card">
        <div slot="header" class="card-header">
          <span class="card-title">基本信息</span>
        </div>
        <el-form-item label="问卷标题" prop="title">
          <el-input
            v-model="form.title"
            placeholder="请输入问卷标题"
            clearable
            class="input-large"
          ></el-input>
        </el-form-item>
        <el-form-item label="适用人群" prop="participants">
          <el-input
            v-model="form.participants"
            placeholder="请输入适用人群"
            clearable
            class="input-large"
          ></el-input>
        </el-form-item>
         <!-- 问卷描述 - 富文本 -->
        <el-form-item label="问卷描述" prop="description">
          <quill-editor
            v-model="form.description"
            :options="editorOptions"
            class="quill-editor"
          ></quill-editor>
        </el-form-item>

        <!-- 问卷结束语 - 富文本 -->
        <el-form-item label="问卷结束语" prop="afterword">
          <quill-editor
            v-model="form.afterword"
            :options="editorOptions"
            class="quill-editor"
          ></quill-editor>
        </el-form-item>
        <el-form-item label="问卷状态" prop="status">
          <el-select v-model="form.status" placeholder="请选择问卷状态">
            <el-option label="上架" :value="1"></el-option>
            <el-option label="下架" :value="2"></el-option>
          </el-select>
        </el-form-item>
      </el-card>

      <!-- 2. 时间配置区域 -->
      <el-card shadow="hover" class="form-card mt-20">
        <div slot="header" class="card-header">
          <i class="el-icon-clock main-color"></i>
          <span class="card-title">时间配置</span>
        </div>
        <el-form-item label="开始时间" prop="start_time">
          <el-date-picker
            v-model="form.start_time"
            type="date"
            placeholder="选择开始时间"
            value-format="yyyy-MM-dd"
            class="input-large"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="结束时间" prop="end_time">
          <el-date-picker
            v-model="form.end_time"
            type="date"
            placeholder="选择结束时间"
            value-format="yyyy-MM-dd"
            class="input-large"
          ></el-date-picker>
        </el-form-item>
      </el-card>

      <!-- 3. 题目配置区域 -->
      <el-card shadow="hover" class="form-card mt-20">
        <div slot="header" class="card-header flex justify-between">
          <div class="flex items-center">
            <i class="el-icon-list main-color"></i>
            <span class="card-title">题目配置</span>
          </div>
        </div>

        <!-- 无题目提示：移到拖拽容器外 -->
        <div
          v-if="form.question.length === 0"
          class="no-question text-center py-30 text-gray-400"
        >
          <i class="el-icon-file-text-o text-40 mb-10"></i>
          <p>暂无题目，请点击「添加题目」按钮创建</p>
        </div>

        <!-- 拖拽容器：仅包含题目项 -->
        <div class="question-list" ref="questionList" v-else>
          <div
            v-for="(question, qIndex) in form.question"
            :key="`question_${question.id || qIndex}_${Date.now()}`"
            class="question-card"
            :data-index="qIndex"
          >
            <div class=" flex justify-between items-center">
              <div class="flex items-center">
                <span class="question-index">题目 {{ qIndex + 1 }}：</span>
                <el-form-item
                  :prop="`question[${qIndex}].title`"
                  :rules="[
                    { required: true, message: '请选择题目内容', trigger: 'blur' }
                  ]"
                  class="mb-0 ml-10"
                >
                  <el-tag  type="warning" style="margin-right: 10px;" v-if="question.question_type_text"> 
                    {{ question.question_type_text }}题
                  </el-tag>  
                  <el-select 
                    v-model="question.id" 
                    placeholder="请选择题目内容" 
                    filterable 
                    class="w500" 
                    @change="handleQuestionChange($event, qIndex)"
                  >
                    <el-option 
                      v-for="item in questionList" 
                      :key="item.id" 
                      :label="item.title" 
                      :value="item.id"
                    >
                      <template>
                        {{ item.title }}——{{ item.question_type }}题
                      </template>
                    </el-option>
                  </el-select>
                  <div v-if="question.options_str" class="options-str mt-5">
                    {{ question.options_str }}
                  </div>
                </el-form-item>
              </div>

              <div class="flex items-center">
                <!-- 排序按钮 -->
                <!-- <el-button
                  type="text"
                  size="small"
                  icon="el-icon-top"
                  @click.stop="moveQuestionUp(qIndex)"
                  :disabled="qIndex === 0"
                ></el-button>
                <el-button
                  type="text"
                  size="small"
                  icon="el-icon-bottom"
                  @click.stop="moveQuestionDown(qIndex)"
                  :disabled="qIndex === form.question.length - 1"
                ></el-button> -->
                <!-- 删除题目 -->
                <el-button
                  type="text"
                  size="small"
                  icon="el-icon-delete"
                  class="text-danger"
                  @click="deleteQuestion(qIndex)"
                ></el-button>
              </div>
            </div>
          </div>
        </div>
        <div style="text-align: right;">
          <el-button
            type="primary"
            size="small"
            icon="el-icon-plus"
            @click="addQuestion"
            class="main-bg"
          >
            添加题目
          </el-button>
        </div>
      </el-card>

      <!-- 4. 高级设置 -->
      <el-card shadow="hover" class="form-card mt-20">
        <div slot="header" class="card-header">
          <i class="el-icon-magic main-color"></i>
          <span class="card-title">高级设置</span>
        </div>
        <el-form-item label="是否弹窗" prop="is_popup">
          <el-select
            v-model="form.is_popup"
            placeholder="是否弹窗展示"
            @change="handlePopupChange"
          >
            <el-option label="否" :value="1"></el-option>
            <el-option label="是" :value="2"></el-option>
          </el-select>
        </el-form-item>
        <!-- 弹窗图片 -->
        <el-form-item
          v-if="form.is_popup == 2"
          label="弹窗图片"
          prop="popup_image"
        >
          <el-upload
            class="upload-demo"
            :action="uploadUrl" 
            :on-success="handleImageUploadSuccess"
            :file-list="popupImageList"
            :on-remove="handleImageRemove"
            list-type="picture-card"
            :limit="1"
            :on-exceed="handleImageExceed"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
          <div class="el-upload__tip mt-5" slot="tip">
            支持JPG/PNG格式，建议尺寸300*200px
          </div>
        </el-form-item>
      </el-card>

      <!-- 提交按钮区域 -->
      <div class="form-submit mt-30">
        <el-button
          type="primary"
          size="medium"
          class="main-bg mr-20"
          @click="submitSurvey"
        >
          保存问卷
        </el-button>
        <el-button type="default" size="medium" @click="resetForm">
          重置表单
        </el-button>
        <el-button 
          type="default" 
          size="medium" 
          @click="$router.go(-1)"
          class="ml-20"
        >
          返回
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Sortable from 'sortablejs'
import { checkQuestion, addSurvey, getSurveyDetail, updateSurvey } from "@/api/sys";
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
export default {
  name: 'CustomSurveyEditor',
  components: {
    quillEditor
  },
  data() {
    return {
      form: {
        title: '',
        participants: '',
        description: '',
        afterword: '',
        content: '[]', 
        start_time: '',
        end_time: '',
        is_popup: 1, 
        popup_image: '',
        operator: '',
        status: 1,
        question: [
          {
            id: '', 
            title: '',
            question_type: '', 
            options_str: '',
            sort: 1
          }
        ]
      },
      questionList: [],
      // 弹窗图片上传相关
      uploadUrl: 'https://sunburn.178590.com/mini/file/bannerFile',
      popupImageList: [],
      // 标记是否为编辑模式
      isEditMode: false,
      // 保存Sortable实例
      sortableInstance: null,
      editorOptions: {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'], // 加粗、斜体、下划线、删除线
            [{ 'align': [] }], // 对齐方式
            [{ 'list': 'ordered' }, { 'list': 'bullet' }], // 有序列表、无序列表
            [{ 'indent': '-1' }, { 'indent': '+1' }], // 缩进
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }], // 标题级别
            [{ 'color': [] }, { 'background': [] }], // 文字颜色、背景颜色
            ['link', 'image', 'video'], // 链接、图片、视频
            ['clean'], // 清除格式
          ]
        },
        placeholder: '请输入问卷结束语...'
      }
    }
  },
  computed: {
    ...mapGetters(['userInfo']),
    validationRules() {
      return {
        title: [
          { required: true, message: '请输入问卷标题', trigger: 'blur' },
          { max: 50, message: '标题长度不能超过50个字符', trigger: 'blur' }
        ],
        participants: [
          { required: true, message: '请输入适用人群', trigger: 'blur' }
        ],
        start_time: [
          { required: true, message: '请选择开始时间', trigger: 'change' }
        ],
        end_time: [
          { required: true, message: '请选择结束时间', trigger: 'change' },
          {
            validator: (rule, value, callback) => {
              if (this.form.start_time && value) {
                if (new Date(value) < new Date(this.form.start_time)) {
                  callback(new Error('结束时间不能早于开始时间'))
                } else {
                  callback()
                }
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ],
        is_popup: [
          { required: true, message: '请选择是否弹窗', trigger: 'change' }
        ],
        popup_image: [
          {
            required: this.form.is_popup == 2,
            message: '请上传弹窗图片',
            trigger: 'change'
          }
        ],
        status: [
          { required: true, message: '请选择问卷状态', trigger: 'change' }
        ]
      }
    }
  },
  mounted() {
    // 初始化表单数据
    this.initFormData();
  },
  watch: {
    // 监听题目列表变化，重新初始化排序
    'form.question': {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.initQuestionSort();
          // 题目变化时更新content
          this.updateContentArray();
        });
      }
    }
  },
  methods: {
    /**
     * 更新content数组，使其与当前题目顺序一致
     */
    updateContentArray() {
      const formattedContent = this.form.question.map(q => q.id);
      this.form.content = JSON.stringify(formattedContent);
      console.log('更新后的content数组:', this.form.content);
    },
    
    /**
     * 初始化表单数据
     */
    initFormData() {
      // 设置操作人
      this.form.operator = this.userInfo?.id || '未知用户';
      
      // 加载题目列表
      this.getQuestionList();
      
      // 如果是编辑模式，加载详情
      const surveyId = this.$route.query.id;
      if (surveyId) {
        this.isEditMode = true;
        this.getDetail(surveyId);
      }
    },
    
    /**
     * 获取问卷详情
     */
    getDetail(id) {
      getSurveyDetail({ id }).then(res => {
          if (res && res.data) {
            this.form = { ...res.data };
            console.log('问卷详情:', this.form,res);
            // 处理题目数据
            if (!this.form.question || !Array.isArray(this.form.question)) {
              this.form.question = [this.createEmptyQuestion()];
            } else {
              // 确保每个题目都有sort字段
              this.form.question.forEach((q, i) => {
                q.sort = q.sort || i + 1;
              });
            }
            // 处理弹窗图片
            if (this.form.popup_image) {
              this.popupImageList = [{
                url: this.form.popup_image,
                name: '弹窗图片'
              }];
            }
            // 初始化content数组
            this.updateContentArray();
          } else {
            this.$message.error('获取问卷详情失败');
          }
        })
        .catch(err => {
          console.error('获取问卷详情失败:', err);
          this.$message.error('获取问卷详情失败，请重试');
        });
    },
    
    /**
     * 处理题目选择变化
     */
    handleQuestionChange(questionId, qIndex) {
      console.log('题目选择变化:', questionId, qIndex)
      if (!questionId) {
        // 清空选择
        this.$set(this.form.question, qIndex, {
          ...this.createEmptyQuestion(),
          sort: this.form.question[qIndex].sort
        });
        return;
      }
      
      const question = this.questionList.find(q => q.id === questionId);
      if (question) {
       this.$set(this.form.question, qIndex, {
          ...this.form.question[qIndex],
          id: question.id,
          title: question.title,
          question_type_text: question.question_type,
          options_str: question.options_str
        });
      } else {
        this.$message.warning('未找到该题目信息');
      }
    },
    
    
    /**
     * 获取题目列表
     */
    getQuestionList() {
      checkQuestion({ status: 1 ,type: 2})
        .then(res => {
          if (Array.isArray(res)) {
            this.questionList = res;
          } else if (res && res.data && Array.isArray(res.data)) {
            this.questionList = res.data;
          } else {
            this.questionList = [];
            this.$message.warning('题目列表格式不正确');
          }
        })
        .catch(err => {
          console.error('获取题目列表失败:', err);
          this.$message.error('获取题目列表失败，请重试');
          this.questionList = [];
        });
    },
    
    /**
     * 创建空的选项对象
     */
    createEmptyOption() {
      return {
        id: '',
        content: ''
      };
    },
    
    /**
     * 创建空的题目对象
     */
    createEmptyQuestion() {
      return {
        id: '',
        title: '',
        question_type_text: '',
        options_str: '',
        sort: this.form.question.length + 1
      };
    },
    
    /**
     * 添加题目
     */
    addQuestion() {
      this.form.question.push(this.createEmptyQuestion());
      // 不需要手动调用initQuestionSort，watch会自动处理
    },
    
    /**
     * 删除题目
     */
    deleteQuestion(index) {
      this.$confirm('确定要删除这个题目吗？删除后不可恢复', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.form.question.splice(index, 1);
        // 更新排序
        this.updateQuestionSort();
        // 更新content数组
        this.updateContentArray();
      }).catch(() => {
        // 取消删除
      });
    },
    
    /**
     * 上移题目
     */
    moveQuestionUp(index) {
      if (index > 0) {
        [this.form.question[index], this.form.question[index - 1]] = 
        [this.form.question[index - 1], this.form.question[index]];
        this.updateQuestionSort();
        // 更新content数组
        this.updateContentArray();
      }
    },
    
    /**
     * 下移题目
     */
    moveQuestionDown(index) {
      if (index < this.form.question.length - 1) {
        [this.form.question[index], this.form.question[index + 1]] = 
        [this.form.question[index + 1], this.form.question[index]];
        this.updateQuestionSort();
        // 更新content数组
        this.updateContentArray();
      }
    },
    
    /**
     * 更新题目排序
     */
    updateQuestionSort() {
      this.form.question.forEach((q, i) => {
        q.sort = i + 1;
      });
      // 强制更新视图
      this.$forceUpdate();
    },
    
    /**
     * 初始化题目拖拽排序
     */
    initQuestionSort() {
      const el = this.$refs.questionList;
      if (!el) return;

      // 销毁已存在的实例
      if (this.sortableInstance) {
        this.sortableInstance.destroy();
        this.sortableInstance = null;
      }

      // 创建新的Sortable实例
      this.sortableInstance = new Sortable(el, {
        animation: 150,
        handle: '.question-card', // 指定拖拽手柄
        ghostClass: 'sortable-ghost', // 拖拽时的占位元素样式
        chosenClass: 'sortable-chosen', // 选中元素的样式
        dragClass: 'sortable-drag', // 拖拽元素的样式
        onStart: (evt) => {
          console.log('开始拖拽 - 原索引:', evt.oldIndex);
        },
        onEnd: (evt) => {
          console.log('结束拖拽 - 原索引:', evt.oldIndex, '新索引:', evt.newIndex);
          
          const { oldIndex, newIndex } = evt;
          if (oldIndex === newIndex) return;

          // 从原位置移除元素
          const [movedQuestion] = this.form.question.splice(oldIndex, 1);
          // 插入到新位置
          this.form.question.splice(newIndex, 0, movedQuestion);
          
          // 更新排序号
          this.updateQuestionSort();
          
          // 拖拽完成后更新content数组
          this.updateContentArray();
          
          // 打印排序结果
          console.log('排序后的题目顺序:', this.form.question.map((q, i) => `${i+1}. ${q.title || '未命名'}`));
        }
      });
    },

    /**
     * 处理图片上传成功
     */
    handleImageUploadSuccess(response, file, fileList) {
      if (response && response.code === 200 && response.data && response.data.file_path) {
        this.form.popup_image = response.data.file_path;
        this.popupImageList = fileList;
        this.$message.success('图片上传成功');
      } else {
        this.$message.error('图片上传失败：' + (response?.msg || '未知错误'));
      }
    },
    
    /**
     * 处理图片移除
     */
    handleImageRemove(file, fileList) {
      this.popupImageList = fileList;
      if (fileList.length === 0) {
        this.form.popup_image = '';
      }
    },
    
    /**
     * 处理图片超出数量限制
     */
    handleImageExceed(files, fileList) {
      this.$message.warning(`最多只能上传1张图片`);
    },
    
    /**
     * 处理弹窗设置变化
     */
    handlePopupChange(value) {
      if (value === 1) {
        this.form.popup_image = '';
        this.popupImageList = [];
      }
    },

    /**
     * 提交问卷
     */
    submitSurvey() {
      this.$refs.surveyForm.validate((isValid) => {
        if (!isValid) return;
        
        // 验证是否至少有一个题目
        if (this.form.question.length === 0 || this.form.question.every(q => !q.id)) {
          this.$message.warning('请至少添加一个有效题目');
          return;
        }
        
        // 确保content数组是最新的
        this.updateContentArray();
        
        this.form.start_time = this.form.start_time.slice(0, 10);
        this.form.end_time = this.form.end_time.slice(0, 10);
        
        const request = this.isEditMode ? updateSurvey(this.form) : addSurvey(this.form);
        
        request
          .then(res => {
            if (res && res.code === 200) {
              this.$message.success(this.isEditMode ? '问卷更新成功' : '问卷创建成功');
              this.$router.push('/procedure/paperList');
            } else {
              this.$message.error('操作失败：' + (res?.msg || '未知错误'));
            }
          })
          .catch(err => {
            console.error('提交问卷失败:', err);
            this.$message.error('网络错误，请重试');
          });
      });
    },
    
    /**
     * 重置表单
     */
    resetForm() {
      this.$confirm('确定要重置表单吗？所有未保存的内容将丢失', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 重置表单验证状态
        if (this.$refs.surveyForm) {
          this.$refs.surveyForm.resetFields();
        }
        
        // 重置表单数据
        this.form = {
          title: '',
          participants: '',
          description: '',
          afterword: '',
          content: '[]',
          start_time: '',
          end_time: '',
          is_popup: 1,
          popup_image: '',
          operator: this.userInfo?.id || '未知用户',
          status: 1,
          question: [this.createEmptyQuestion()]
        };
        
        // 重置图片上传列表
        this.popupImageList = [];
      }).catch(() => {
        // 取消重置
      });
    }
  },
  beforeDestroy() {
    // 组件销毁前销毁Sortable实例
    if (this.sortableInstance) {
      this.sortableInstance.destroy();
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .el-tag.el-tag--success {
  color: #33bea7 !important;
}

.survey-editor-container {
  padding: 20px;
}

.form-card {
  border-radius: 8px;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  .card-header {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    padding: 12px 20px;
    .el-icon {
      margin-right: 8px;
      font-size: 16px;
    }
  }
}

.mt-5 { margin-top: 5px; }
.mt-10 { margin-top: 10px; }
.mt-20 { margin-top: 20px; }
.mt-30 { margin-top: 30px; }
.mb-0 { margin-bottom: 0; }
.mb-5 { margin-bottom: 5px; }
.mr-10 { margin-right: 10px; }
.mr-20 { margin-right: 20px; }
.ml-10 { margin-left: 10px; }
.ml-20 { margin-left: 20px; }
.ml-40 { margin-left: 260px; }

.flex { display: flex; }
.justify-between { justify-content: space-between; }
.items-center { align-items: center; }

.input-large { width: 500px; }
.input-medium { width: 350px; }
.input-small { width: 250px; }
.w-80 { width: 80px; }
.w500 { width: 500px; }

.question-list {
  min-height: 60px;
}

.question-card {
  padding: 15px;
  margin-bottom: 15px;
  background-color: #fafafa;
  border-radius: 6px;
  border: 1px solid #eee;
  transition: all 0.2s;
  cursor: grab;
  user-select: none;
  
  &:hover {
    border-color: #2dc0a3;
    box-shadow: 0 2px 8px rgba(45, 192, 163, 0.1);
  }
  
  &:active {
    cursor: grabbing;
  }
}

// 拖拽相关样式
::v-deep .sortable-ghost {
  opacity: 0.5;
  background-color: #e6f7ff;
  border: 1px dashed #2dc0a3;
}

::v-deep .sortable-chosen {
  background-color: #f0f9f7;
  border-color: #2dc0a3;
}

.question-index {
  font-weight: 600;
  color: #333;
  min-width: 60px;
  text-align: center;
}

.options-str {
  color: #666;
  padding: 5px 10px;
  background-color: #f0f9f7;
  border-radius: 4px;
  font-size: 14px;
}

.no-question {
  font-size: 14px;
  .el-icon {
    color: #ccc;
  }
}

.form-submit {
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: right;
}

.text-danger { color: #f56c6c; }
.text-gray-400 { color: #999; }

::v-deep .el-upload--picture-card {
  width: 100px;
  height: 100px;
  line-height: 100px;
}

::v-deep .el-upload-list--picture-card .el-upload-list__item {
  width: 100px;
  height: 100px;
}

::v-deep .el-button + .el-button {
  margin-left: 10px;
}
</style>
